import { motion } from "framer-motion";
import {
  Stethoscope, HeartPulse, Brain, Salad, BarChart3, Check,
  PiggyBank, TrendingUp, ShieldPlus, LineChart, Lock,
  Anchor, Plane, Factory, Fuel, Truck, Building2, Hospital, HardHat,
} from "lucide-react";
import { Reveal, MaskedLines, Stagger, StaggerItem } from "@/components/site/Reveal";

const services = [
  {
    icon: Stethoscope,
    title: "Pre-Employment Health Check-up (PEHC)",
    desc: "Comprehensive medical evaluation to determine an individual's fitness before joining employment.",
    label: "Includes",
    items: ["Medical Examination", "Fitness Assessment", "Medical Certification"],
  },
  {
    icon: HeartPulse,
    title: "Annual Health Check-up (AHC)",
    desc: "Customized health screening packages designed for early detection and preventive healthcare.",
    label: "Designed For",
    items: ["Corporate Employees", "Marine Staff"],
  },
  {
    icon: Brain,
    title: "Psychological Assessment",
    desc: "Scientifically validated behavioural and personality assessments conducted by qualified psychologists.",
    label: "Assessment Tools",
    items: ["Behavioural Assessment", "Cognitive Evaluation", "Ideal for safety-critical positions", "High-responsibility occupations"],
  },
  {
    icon: Salad,
    title: "Clinical Nutrition & Lifestyle Management",
    desc: "Where medical findings indicate lifestyle-related health risks, MedVerify provides individualized wellness interventions.",
    label: "Programs Include",
    items: ["Weight Management", "Diabetes Management", "Hypertension Counselling", "Lifestyle Coaching"],
  },
  {
    icon: BarChart3,
    title: "Corporate Health Analytics",
    desc: "Transform employee health data into meaningful business insights.",
    label: "Organizations Receive",
    items: ["Health Risk Analysis", "Disease Trend Reports", "Workforce Health Dashboard", "Department-wise Risk Analysis"],
  },
];

const whyItems = [
  { icon: PiggyBank, title: "Reduce Healthcare Costs", desc: "Early identification of health risks helps reduce long-term medical expenditure." },
  { icon: TrendingUp, title: "Improve Productivity", desc: "A healthier workforce results in fewer sick days and improved operational efficiency." },
  { icon: ShieldPlus, title: "Prevent Lifestyle Diseases", desc: "Early intervention programs help reduce diabetes, hypertension, obesity and cardiovascular disease." },
  { icon: LineChart, title: "Data-Driven Decision Making", desc: "Interactive health analytics support HR and management in planning targeted wellness initiatives." },
  { icon: Lock, title: "Confidential & Secure", desc: "Employee medical records are securely stored using encrypted cloud infrastructure with strict confidentiality." },
];

const industries = [
  { icon: Anchor, name: "Maritime & Shipping" },
  { icon: Plane, name: "Aviation" },
  { icon: Factory, name: "Manufacturing" },
  { icon: Fuel, name: "Oil & Gas" },
  { icon: Truck, name: "Logistics" },
  { icon: Building2, name: "Corporate Organizations" },
  { icon: Hospital, name: "Healthcare" },
  { icon: HardHat, name: "Construction" },
];

export default function CorporateWellness() {
  return (
    <main data-testid="wellness-page">
      {/* HERO */}
      <section className="pt-44 pb-20 md:pb-28 bg-[#12294d] hero-grain relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="overline-label mb-6">
            Comprehensive Health Screening • Risk Assessment • Employee Wellness Programs
          </motion.p>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.15] max-w-4xl" data-testid="wellness-heading">
            <MaskedLines lines={["Occupational Health &", "Employee Wellness Solutions"]} startDelay={0.15} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mt-8 text-lg md:text-xl text-[#2ab5cf] font-semibold">
            Protecting Your Workforce. Enhancing Productivity.
          </motion.p>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-lg md:text-xl" data-testid="wellness-intro"><h3 className="font-bold text-[#2ab5cf] mb-2">Corporate Wellness</h3><p className="text-slate-600 leading-relaxed">MedVerify Services provides Corporate Wellness services, including end-to-end Occupational Health services:</p><ul className="mt-3 space-y-2 list-disc pl-6 text-slate-600 leading-relaxed">
                <li>Promote organizational ROI</li>
                <li>Enhance employee productivity</li>
                <li>Reduce absenteeism</li>
                <li>Support employee wellbeing</li>
                <li>Offer regulatory compliance best practices</li>
                <li>Enable organizations to make informed strategic decisions regarding their employees</li>
              </ul></div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 md:py-28 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-display font-bold text-[#12294d] text-4xl md:text-5xl tracking-tight">Corporate Wellness Services</h2>
            <p className="mt-5 text-lg md:text-xl text-slate-500 font-medium">Workforce health assessments</p>
          </Reveal>
          <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((s, i) => (
              <StaggerItem key={i}>
                <div className="card-lift bg-white border border-slate-200 rounded-xl p-8 h-full flex flex-col" data-testid={`wellness-service-${i}`}>
                  <span className="h-16 w-16 rounded-full bg-[#2ab5cf]/12 flex items-center justify-center">
                    <s.icon size={32} className="text-[#2ab5cf]" />
                  </span>
                  <h3 className="font-display text-[#1a3a6b] text-xl mt-6 tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                  <p className="mt-6 text-xs uppercase tracking-[0.2em] font-bold text-slate-400">{s.label}</p>
                  <ul className="mt-3 space-y-2.5">
                    {s.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm font-medium text-slate-700">
                        <span className="mt-0.5 h-5 w-5 rounded-full bg-[#2ab5cf]/15 flex items-center justify-center shrink-0">
                          <Check size={12} strokeWidth={3} className="text-[#2ab5cf]" />
                        </span>
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* WHY */}
      <section className="py-20 md:py-28 bg-[#12294d] hero-grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <Reveal>
            <h2 className="font-display font-bold text-white text-4xl md:text-5xl tracking-tight">Why Organizations Choose MedVerify</h2>
          </Reveal>
          <Stagger className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyItems.map((w, i) => (
              <StaggerItem key={i}>
                <div className="card-lift bg-white border border-slate-200 rounded-lg p-8 h-full" data-testid={`wellness-why-${i}`}>
                  <w.icon size={26} className="text-[#1a3a6b]" />
                  <p className="mt-5 text-[#12294d] font-bold text-lg">{w.title}</p>
                  <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{w.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-display font-bold text-[#12294d] text-4xl md:text-5xl tracking-tight">Industries We Serve</h2>
          </Reveal>
          <Stagger className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6">
            {industries.map((ind, i) => (
              <StaggerItem key={i}>
                <div className="card-lift bg-[#f8fafc] border border-slate-200 rounded-lg p-8 h-full text-center" data-testid={`wellness-industry-${i}`}>
                  <span className="mx-auto h-14 w-14 rounded-full bg-[#2ab5cf]/15 flex items-center justify-center">
                    <Check size={26} strokeWidth={3} className="text-[#2ab5cf]" />
                  </span>
                  <p className="mt-5 font-bold text-[#12294d]">{ind.name}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </main>
  );
}
